import { defineStore, Pinia } from 'pinia'
import type { Facet, FacetGroup } from '~/apps/facets/domain/facetGroups'
import { FacetGroupRepositoryJson } from '~/apps/facets/repository/facet-groups/FacetGroupRepositoryJson'

export const useFacetsStore = defineStore('facetsStore', () => {
  const repo = new FacetGroupRepositoryJson()
  const facetGroups = ref<FacetGroup[] | null>(null)
  const isLoading = ref(false)

  const pitchtypeFacetSlugs = computed(() => {
    if (!facetGroups.value) return []
    return facetGroups.value.flatMap((group) =>
      group.facets.filter((f) => f.is_pitch_type_facet).map((f) => f.slug),
    )
  })

  // Cached lookup map - computed once when facetGroups changes
  const facetLookup = computed<Record<string, Facet>>(() => {
    try {
      if (!facetGroups.value) {
        return {}
      }
      return facetGroups.value.reduce(
        (acc, group) => {
          group.facets.forEach((facet) => {
            acc[facet.slug] = facet
          })
          return acc
        },
        {} as Record<string, Facet>,
      )
    } catch (error) {
      console.warn('facetLookup computation error:', error)
      return {}
    }
  })

  async function getFacetGroups(lang: string) {
    // If already loaded, return immediately
    if (facetGroups.value) {
      return
    }

    // Prevent concurrent loading
    if (isLoading.value) {
      // Wait for the current loading to finish
      while (isLoading.value) {
        await new Promise((resolve) => setTimeout(resolve, 50))
      }
      return
    }

    isLoading.value = true
    try {
      facetGroups.value = await repo.getFacetGroups(lang)
      // should be sorted already but just in case
      if (facetGroups.value) {
        facetGroups.value.sort((a, b) => a.order - b.order)
      }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Get a facet by its slug using cached O(1) lookup
   * @param slug - The facet slug to look up
   * @returns The facet if found and data is loaded, undefined otherwise
   * @remarks Returns undefined if getFacetGroups() hasn't completed yet.
   * This prevents unhandled promise rejections when called during initialization.
   */
  function facetsBySlug(slug: string): Facet | undefined {
    try {
      // Early return if data not loaded - prevents accessing empty lookup during initialization
      if (facetGroups.value === null) {
        return undefined
      }
      return facetLookup.value[slug]
    } catch (error) {
      // Log error but don't throw - fail gracefully
      console.warn('facetsBySlug error:', error, slug)
      return undefined
    }
  }

  return {
    facetGroups,
    facetsBySlug,
    pitchtypeFacetSlugs,
    getFacetGroups,
    isLoading,
  }
})

export function useFacetsStoreSetup(pinia: Pinia) {
  const facetsStore = useFacetsStore(pinia)
  return facetsStore
}
